import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  Form,
  FormGroup,
  Input,
  Badge,
} from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { api } from "../../../../api";
import Cookies from 'js-cookie';

const EditRole = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState({ role_name: "", capacity: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [isCustomized, setIsCustomized] = useState(false);
  const [originalRoleName, setOriginalRoleName] = useState("");
  const [dashboardPath, setDashboardPath] = useState("");
  const [formErrors, setFormErrors] = useState({ role_name: "", capacity: "" });
  const [touched, setTouched] = useState({ role_name: false, capacity: false });
  const company_id = Cookies.get('company_id');

  useEffect(() => {
    fetchRoleData();
  }, [roleId]);

  const fetchRoleData = async () => {
    try {
      setLoading(true);
      
      // First, get the role data from the existing API
      const response = await api.get(`editRole.php?role_id=${roleId}`);
      
      if (response.data.error) {
        setError(response.data.error);
        return;
      }
      
      const roleData = response.data;
      setRole({
        role_name: roleData.role_name || "",
        capacity: roleData.capacity || "",
      });
      
      // Now check if this role is customized for the company
      try {
        const roleConfigResponse = await api.get(`roleConfig.php?company_id=${company_id}`);
        if (roleConfigResponse.data.status === 'success') {
          const roleConfig = roleConfigResponse.data.data.find(
            r => r.role_name === roleData.role_name
          );
          
          if (roleConfig) {
            setIsCustomized(roleConfig.is_customized);
            setOriginalRoleName(roleData.role_name);
            setDashboardPath(roleConfig.dashboard_path);
          }
        }
      } catch (configError) {
        console.log("Role config not available, using default");
      }
      
    } catch (error) {
      console.error("Error fetching role data:", error);
      setError("Failed to load role data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRole((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear error for the field once user starts typing/selecting
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  };

  const validateForm = () => {
    const errors = { role_name: "", capacity: "" };
    if (!role.role_name || role.role_name.trim() === "") {
      errors.role_name = "Role name is required";
    }
    if (!role.capacity || role.capacity.trim() === "") {
      errors.capacity = "Capacity is required";
    }
    setFormErrors(errors);
    return Object.values(errors).every((v) => v === "");
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!validateForm()) {
      setTouched({ role_name: true, capacity: true });
      return;
    }

    try {
      setError("");
      setSuccessMessage("");
      
      // First, update the role in the existing system
      const payload = {
        role_id: roleId,
        role_name: role.role_name,
        capacity: role.capacity,
      };
      
      const response = await api.post("editRole.php", payload);
      
      if (response.data.error) {
        setError(response.data.error);
        return;
      }
      
      // If role name changed, update the dynamic role configuration
      if (role.role_name !== originalRoleName) {
        try {
          // Determine the correct default role name based on the dashboard path
          let defaultRoleName = originalRoleName;
          
          // Map role names to their default counterparts
          const roleMapping = {
            'Project Engineers': 'Project Manager',
            'Project Engineer': 'Project Manager',
            'General Managers': 'General Manager',
            'General Manager': 'General Manager',
            'HR Managers': 'HR',
            'HR Manager': 'HR',
            'Store Managers': 'Store Manager',
            'Store Manager': 'Store Manager',
            'Site Managers': 'Site Manager',
            'Site Manager': 'Site Manager',
            'Site Supervisors': 'Site Supervisor',
            'Site Supervisor': 'Site Supervisor'
          };
          
          // Use the mapping or fall back to original name
          defaultRoleName = roleMapping[role.role_name] || originalRoleName;
          
          const configResponse = await api.post("roleConfig.php", {
            company_id: company_id,
            default_role_name: defaultRoleName,
            custom_role_name: role.role_name
          });
          
          setSuccessMessage(configResponse.data.message || "Role updated successfully! This change will affect dashboard routing for your company.");
        } catch (configError) {
          console.error("Error updating role configuration:", configError);
          setSuccessMessage("Role updated successfully! (Note: Dynamic routing update failed)");
        }
      } else {
        setSuccessMessage("Role updated successfully!");
      }
      
      setShowSuccessAlert(true);
      setTimeout(() => {
        setShowSuccessAlert(false);
        navigate("/hr-admin/hr-management/role3");
      }, 3000);
      
    } catch (error) {
      console.error("Error updating role:", error);
      setError("Failed to update role");
    }
  };

  const handleCancel = () => {
    navigate("/hr-admin/hr-management/role3");
  };

  if (loading) {
    return (
      <div className="header bg-gradient-info py-7 py-lg-8">
        <Container fluid>
          <Row className="justify-content-center">
            <Col md="8">
              <Card>
                <CardHeader>
                  <h3>Loading...</h3>
                </CardHeader>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error && !role.role_name) {
    return (
      <div className="header bg-gradient-info py-7 py-lg-8">
        <Container fluid>
          <Row className="justify-content-center">
            <Col md="8">
              <Card>
                <CardHeader>
                  <h3>Error</h3>
                  <p>{error}</p>
                  <Button
                    style={{
                      backgroundColor: "#d86f89",
                      borderColor: "#d86f89",
                      color: "white",
                    }}
                    onClick={handleCancel}
                  >
                    Back to Roles
                  </Button>
                </CardHeader>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <>
      <div className="header bg-gradient-info py-7 py-lg-8">
        <Container fluid>
          <Row className="mb-5">
            <Col className="order-xl-2 mb-5 mb-xl-0 text-left">
              <h1 className="text-white">Edit Role</h1>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md="8">
              <Card
                style={{
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                  backgroundColor: "#fff",
                  borderRadius: "15px",
                }}
              >
                <CardHeader>
                  <h3 style={{ fontWeight: "bolder", textTransform: "uppercase" }}>
                    Edit Role: {role.role_name}
                  </h3>
                  {dashboardPath && (
                    <div style={{ marginTop: '10px' }}>
                      <Badge 
                        color={isCustomized ? 'success' : 'secondary'}
                        style={{ fontSize: '12px', marginRight: '10px' }}
                      >
                        {isCustomized ? 'Customized' : 'Default'}
                      </Badge>
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        <FontAwesomeIcon icon={faCog} style={{ marginRight: '5px' }} />
                        Dashboard: <code>{dashboardPath}</code>
                      </span>
                    </div>
                  )}
                </CardHeader>
                <div className="card-body">
                  {/* Error Alert */}
                  {error && (
                    <div className="alert alert-danger text-center" role="alert">
                      {error}
                    </div>
                  )}

                  <Form onSubmit={handleFormSubmit}>
                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-role">
                            Role Name <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className={`form-control-alternative ${formErrors.role_name && touched.role_name ? 'is-invalid' : ''}`}
                            id="input-role"
                            type="text"
                            name="role_name"
                            pattern="^[a-zA-Z ]+$"
                            title="Enter letters only"
                            value={role.role_name}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('role_name')}
                            placeholder="Enter Role Name"
                            required
                          />
                          {formErrors.role_name && touched.role_name && (
                            <div className="invalid-feedback d-block">{formErrors.role_name}</div>
                          )}
                          {role.role_name !== originalRoleName && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '10px', 
                              backgroundColor: '#fff3cd', 
                              border: '1px solid #ffeaa7',
                              borderRadius: '5px',
                              fontSize: '12px'
                            }}>
                              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '5px', color: '#856404' }} />
                              <strong>Note:</strong> Changing this role name will update dashboard routing for your company only. 
                              Other companies using the default role name will not be affected.
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-capacity">
                            Capacity <span style={{ color: "red" }}>*</span>
                          </label>
                          <Input
                            className={`form-control-alternative ${formErrors.capacity && touched.capacity ? 'is-invalid' : ''}`}
                            id="input-capacity"
                            type="select"
                            name="capacity"
                            value={role.capacity}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('capacity')}
                            required
                          >
                            <option value="">Select Capacity</option>
                            <option value="Not Applicable">Not Applicable</option>
                            {[...Array(10)].map((_, i) => (
                              <option key={i + 1} value={i + 1}>
                                {i + 1}
                              </option>
                            ))}
                          </Input>
                          {formErrors.capacity && touched.capacity && (
                            <div className="invalid-feedback d-block">{formErrors.capacity}</div>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12" style={{ textAlign: "center" }}>
                        <Button
                          style={{
                            backgroundColor: "#d86f89",
                            borderColor: "#d86f89",
                            color: "white",
                            marginRight: "10px",
                          }}
                          type="submit"
                        >
                          Update Role
                        </Button>
                        <Button
                          style={{
                            backgroundColor: "#6c757d",
                            borderColor: "#6c757d",
                            color: "white",
                          }}
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </Col>
                    </Row>
                    
                    {/* Information Section */}
                    {/* <Row style={{ marginTop: '30px' }}>
                      <Col lg="12">
                        <div style={{ 
                          padding: '15px', 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: '8px',
                          border: '1px solid #e9ecef',
                          fontSize: '13px'
                        }}>
                          <h6 style={{ color: '#333', marginBottom: '10px' }}>
                            <FontAwesomeIcon icon={faCog} style={{ marginRight: '8px' }} />
                            Dynamic Role Configuration
                          </h6>
                          <ul style={{ margin: 0, paddingLeft: '20px', color: '#666' }}>
                            <li>Role name changes only affect your company's dashboard routing</li>
                            <li>Other companies using the same default role name will not be affected</li>
                            <li>Dashboard paths remain the same - only the role name changes</li>
                            <li>Changes take effect immediately for new logins</li>
                          </ul>
                        </div>
                      </Col>
                    </Row> */}
                  </Form>
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Success Alert Modal */}
      {showSuccessAlert && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'flex-start', paddingTop: '15%', zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff', padding: '30px', borderRadius: '15px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)', textAlign: 'center',
            maxWidth: '400px', width: '90%', border: '3px solid #d86f89'
          }}>
            <div style={{ fontSize: '48px', color: '#d86f89', marginBottom: '20px' }}>✓</div>
            <h4 style={{ color: '#28a745', marginBottom: '15px' }}>Success!</h4>
            <p style={{ color: '#333', fontSize: '16px', marginBottom: '20px' }}>{successMessage}</p>
            <Button
              style={{
                backgroundColor: '#d86f89', borderColor: '#d86f89', color: 'white',
                padding: '10px 25px', borderRadius: '25px'
              }}
              onClick={() => setShowSuccessAlert(false)}
            >OK</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default EditRole; 